import { hslToRgb, rgbToHsl } from "./colorUtils";

/**
 * Apply color shift to image data
 * @param imageData - ImageData to modify
 * @param hue - Hue shift in degrees
 * @param saturation - Saturation multiplier
 * @param preset - Optional preset color shift
 * @returns Modified ImageData
 */
export function applyColorShiftToImageData(
	imageData: ImageData,
	hue: number,
	saturation: number,
	preset?: "Cyanopsia" | "Xanthopsia",
): ImageData {
	const data = imageData.data;

	for (let i = 0; i < data.length; i += 4) {
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		if (r !== undefined && g !== undefined && b !== undefined) {
			let newR = r;
			let newG = g;
			let newB = b;

			if (preset === "Cyanopsia") {
				// Cyanopsia: shift to cyan (increase green and blue, decrease red)
				newR = Math.max(0, r * 0.5);
				newG = Math.min(255, g * 1.5);
				newB = Math.min(255, b * 1.5);
			} else if (preset === "Xanthopsia") {
				// Xanthopsia: shift to yellow (increase red and green, decrease blue)
				newR = Math.min(255, r * 1.5);
				newG = Math.min(255, g * 1.5);
				newB = Math.max(0, b * 0.5);
			} else {
				// Custom hue and saturation adjustment
				// Convert RGB to HSL, adjust, then back to RGB
				const hsl = rgbToHsl(r, g, b);
				hsl[0] = (hsl[0] + hue) % 360; // Adjust hue
				hsl[1] = Math.max(0, Math.min(1, hsl[1] * saturation)); // Adjust saturation
				const rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);
				newR = rgb[0];
				newG = rgb[1];
				newB = rgb[2];
			}

			data[i] = Math.min(255, Math.max(0, newR)); // Red
			data[i + 1] = Math.min(255, Math.max(0, newG)); // Green
			data[i + 2] = Math.min(255, Math.max(0, newB)); // Blue
			// Alpha channel remains unchanged
		}
	}
	return imageData;
}
